import type { EditorConfig } from "@prismicio/editor-fields"
import { FatalError } from "@prismicio/editor-support/Error"
import type { StaticCustomType } from "@prismicio/types-internal/lib/customtypes"
import { type ReactNode, useContext, useMemo } from "react"

import { staticEditorConfig } from "../app/editorConfig"
import { useAiSeoMetadataFlag } from "../app/integrations/AmplitudeExperiments"
import { useContentOffset } from "../app/layout/useOffset"
import { trackMetadataGeneration } from "../app/services/tracking"
import { ContributorAlert } from "./ContributorAlert"
import {
  DocumentContext,
  type DocumentContextValue,
  type ExistingDocumentContextValue,
  type NewDocumentContextValue,
} from "./DocumentContext"
import { type DocumentData } from "./documentData"
import { useDocumentParams } from "./documentRouting"
import { useDocumentTitle } from "./useDocumentTitle"
import { useDraft } from "./useDraft"

interface Props {
  children: ReactNode
  initData: DocumentData
}

function isExistingDocumentContext(
  data: DocumentContextValue,
): data is ExistingDocumentContextValue {
  return data.type === "existing"
}

function isNewDocumentContext(
  data: DocumentContextValue,
): data is NewDocumentContextValue {
  return data.type === "new"
}

export function useExistingDocument() {
  const document = useDocument()

  if (!isExistingDocumentContext(document))
    throw new Error(
      `Invalid document type. Expected existing, got ${document.type}.`,
    )

  return document
}

export function useNewDocument() {
  const document = useDocument()

  if (!isNewDocumentContext(document))
    throw new Error(
      `Invalid document type. Expected new, got ${document.type}.`,
    )

  return document
}

export function useDocument() {
  const document = useContext(DocumentContext)

  if (!document)
    throw new Error("useDocument must be used within a DocumentProvider")

  return document
}

export function DocumentProvider(props: Props) {
  const { children, initData } = props
  const { customType } = initData

  const { section } = useDocumentParams()
  const draft = useDraft(initData)
  const documentTitle = useDocumentTitle(initData, draft)
  const aiSeoMetadataFlag = useAiSeoMetadataFlag()

  const { contentOffset } = useContentOffset()

  const type = initData.type

  const status = type === "existing" ? initData.version.status : "unclassified"

  const locale =
    type === "existing" ? initData.meta.language.id : initData.locale

  const documentId = type === "existing" ? initData.meta.id : undefined

  const config: EditorConfig = useMemo(
    () => ({
      ...staticEditorConfig,
      status,
      documentId,
      localeId: locale,
      customTypeId: customType.id,
      ...getAISeoConfig(aiSeoMetadataFlag),
      scrollOffsetTop: contentOffset,
    }),
    [
      status,
      locale,
      customType.id,
      documentId,
      aiSeoMetadataFlag,
      contentOffset,
    ],
  )

  const groupLangId =
    type === "new" ? initData.groupLangId : initData.meta.group_lang_id

  const { sectionNames, sectionName } = getSectionName({ customType, section })

  if (sectionName === undefined) {
    throw new FatalError("Unknown custom type's section")
  }

  const edited = Boolean(draft.editedAt)

  const baseContextValue = {
    config,
    customType,
    draft,
    sectionNames,
    sectionName,
    edited,
    edit: draft.edit,
    content: draft.document,
    locale,
    status,
    groupLangId,
    documentTitle,
  }

  if (type === "existing") {
    return (
      <DocumentContext.Provider
        value={{
          ...baseContextValue,
          type,
          documentId: initData.meta.id,
          data: initData,
          versions: initData.meta.versions,
          version: initData.version,
          title: initData.meta.title,
        }}
      >
        {children}
        <ContributorAlert />
      </DocumentContext.Provider>
    )
  }

  return (
    <DocumentContext.Provider
      value={{
        ...baseContextValue,
        type,
        data: initData,
        versions: [],
        releaseId: initData.releaseId,
        title: initData.asCopy?.title,
      }}
    >
      {children}
      <ContributorAlert />
    </DocumentContext.Provider>
  )
}

function getSectionName(args: {
  customType: StaticCustomType
  section?: string
}) {
  const { customType, section = "" } = args

  const sectionNames = Object.keys(customType.json)
  const sectionName = section in customType.json ? section : sectionNames[0]

  return { sectionNames, sectionName }
}

function getAISeoConfig(
  aiSeoMetadataFlag: ReturnType<typeof useAiSeoMetadataFlag>,
) {
  if (aiSeoMetadataFlag === "off") return {}

  return {
    aiSeoMetadataEnabled: true,
    trackMetadataGeneration,
  }
}
